import * as React from 'react';

import { cn } from '@/lib/utils';

/* Dre / LinhaDre — a mesma conta, na linguagem do contador.
 *
 * Uma DRE é uma sequência de subtrações com dois totais no meio do caminho.
 * O que a torna legível não é a tabela: é (1) o operador na margem esquerda,
 * que diz se aquela linha soma, subtrai ou fecha, e (2) o campo de cor nos
 * totais, que faz o olho achar as duas linhas que importam sem contar as
 * outras.
 *
 * Por que não é uma `<table>`: não há duas dimensões aqui. É uma lista de
 * lançamentos com valor e participação; marcar como tabela obrigaria o leitor
 * de tela a navegar por células de uma grade que não existe.
 *
 * A coluna de valor tem largura mínima fixa para que os dígitos alinhem entre
 * linhas mesmo com nomes de tamanhos diferentes. Número é dado. */

export type OperadorDre = 'nenhum' | 'mais' | 'menos' | 'igual';

/** Realce da linha. Totais recebem campo; lançamentos, não. */
export type CampoLinhaDre = 'nenhum' | 'areia' | 'mint' | 'mel' | 'telha';

export type TomLinhaDre = 'neutro' | 'positivo' | 'negativo';

const MENOS = '−'; // minus tipográfico, não hífen: alinha com os dígitos

const SINAL: Record<OperadorDre, string> = {
  nenhum: '',
  mais: '+',
  menos: MENOS,
  igual: '=',
};

const CAMPO: Record<CampoLinhaDre, string | undefined> = {
  nenhum: undefined,
  areia: 'bg-areia',
  mint: 'bg-mint',
  mel: 'bg-mel',
  telha: 'bg-telha',
};

const TOM: Record<TomLinhaDre, string | undefined> = {
  neutro: undefined,
  positivo: 'text-positivo',
  negativo: 'text-negativo',
};

export function Dre({ className, ...resto }: React.ComponentProps<'div'>) {
  return <div className={cn('grid gap-0.5', className)} {...resto} />;
}

export type LinhaDreProps = {
  nome: string;
  /** Uma linha dizendo de onde a conta veio. */
  nota?: string | undefined;
  /** Já formatado. Normalmente um `<Valor formato="moeda">`. */
  valor: React.ReactNode;
  /** Participação sobre a receita. Já formatada. */
  percentual?: React.ReactNode;
  operador?: OperadorDre | undefined;
  campo?: CampoLinhaDre | undefined;
  /** Tinge valor e percentual. Use no resultado, não em cada lançamento. */
  tom?: TomLinhaDre | undefined;
  /** Linha de total: nome em peso maior. */
  total?: boolean | undefined;
  className?: string | undefined;
};

export function LinhaDre({
  nome,
  nota,
  valor,
  percentual,
  operador = 'menos',
  campo = 'nenhum',
  tom = 'neutro',
  total = false,
  className,
}: LinhaDreProps) {
  return (
    <div
      className={cn(
        'grid grid-cols-[16px_1fr_auto] items-baseline gap-x-3 gap-y-0.5 rounded-lg px-3 py-2',
        // `mesa:` (janela) de propósito, única exceção à regra de grade por
        // contêiner: o DRE vive numa fração de coluna que o @container do
        // casco não mede (ele mediria a coluna inteira e abriria a quarta
        // coluna num cartão estreito). Aqui a pergunta é densidade de
        // leitura, e densidade é decisão de janela. `grades.test.ts` cobre
        // as telas, não este arquivo.
        'mesa:grid-cols-[16px_1fr_auto_auto]',
        CAMPO[campo],
        className,
      )}
    >
      <span className="text-tinta-3 numero text-center text-apoio" aria-hidden="true">
        {SINAL[operador]}
      </span>

      <span className={cn('min-w-0 text-apoio', total ? 'font-semibold' : undefined)}>
        {nome}
        {nota ? <small className="text-tinta-2 mt-px block text-nota">{nota}</small> : null}
      </span>

      <span
        className={cn(
          'numero min-w-coluna-valor text-right text-apoio font-semibold whitespace-nowrap',
          TOM[tom],
        )}
      >
        {valor}
      </span>

      {percentual ? (
        <span
          className={cn(
            'numero col-span-2 col-start-2 min-w-coluna-pct text-nota mesa:col-span-1 mesa:col-start-4 mesa:text-right',
            TOM[tom] ?? 'text-tinta-2',
          )}
        >
          {percentual}
        </span>
      ) : null}
    </div>
  );
}
